/**
 * project-data.js
 * Pure utility functions for processing & displaying TerraIndex project data.
 * No LitElement / DOM dependencies — safe to import anywhere.
 */

import { tiCodelists } from '../ti-codelists.js';
import { extractText, getSoilLabel } from '@mv/models';

export { extractText };

/** Format a soil string: deduplicate, split on ; or |, remove junk */
export function formatSoilString(waarde) {
  const str = extractText(waarde);
  if (!str || str === '-' || str === 'Onbekend') return null;
  const parts = str.split(/[;|]/)
    .map(p => p.trim())
    .filter(p => p && p !== '-' && p !== 'Onbekend' && p.toLowerCase() !== 'null' && !/^\d$/.test(p));
  const unique = [...new Set(parts)];
  return unique.length > 0 ? unique.join('; ') : null;
}

// ─── Data structuring ───────────────────────────────────────────────────────

/**
 * Turn raw combined { Meetpunten, Lagen, Monsters, Project } data into a
 * structured { project, borings, samples, rawData } object for display.
 */
export function formatStructuredData(data) {
  const structured = { project: null, borings: [], samples: [], rawData: data };

  if (data.Project) {
    structured.project = {
      name: data.Project.name?.waarde || 'Onbekend',
      code: data.Project.projectCode?.waarde || 'Onbekend',
    };
  }

  if (data.Meetpunten?.length) {
    data.Meetpunten.forEach((meetpunt, mpIndex) => {
      const boringName = meetpunt.name?.waarde || meetpunt.identificatie?.waarde || `Boring ${mpIndex + 1}`;
      const layers = [];

      data.Lagen?.forEach((laag, layerIndex) => {
        const layerBoring = laag.meetpuntName?.waarde || laag.gekoppeld_meetpunt?.waarde || laag.boring?.waarde;
        if (layerBoring === boringName || (!layerBoring && mpIndex === 0)) {
          layers.push({
            id: layerIndex,
            depth: {
              from: laag.upperDepth_cm?.waarde || 0,
              to: laag.lowerDepth_cm?.waarde || 0,
            },
            soilType: getSoilLabel(laag.grondsoort?.sikb_id ?? laag.grondsoort?.waarde) || 'Onbekend',
            bodemkenmerk: laag.bodemkenmerk?.waarde,
            bodemkenmerkGradatie: laag.bodemkenmerkGradatie?.waarde,
            bzb: laag.bzb,
            color: laag.kleur?.waarde,
            description: laag.opmerking?.waarde,
          });
        }
      });

      layers.sort((a, b) => a.depth.from - b.depth.from);

      structured.borings.push({
        id: mpIndex,
        name: boringName,
        type: meetpunt.typeOnderzoek?.waarde || 'Onbekend',
        coordinates: meetpunt.coordinaten?.waarde,
        layers,
        layerCount: layers.length,
      });
    });
  }

  data.Monsters?.forEach((monster, index) => {
    structured.samples.push({
      id: index,
      name: monster.naam?.waarde || monster.identificatie?.waarde || null,
      type: monster.typeMonster?.waarde,
      depth: {
        from: monster.upperDepth_cm?.waarde || monster.diepteVan?.waarde,
        to: monster.lowerDepth_cm?.waarde || monster.diepteTot?.waarde,
      },
      date: monster.datum?.waarde,
      boring: monster.gekoppeld_meetpunt?.waarde,
      description: monster.omschrijving?.waarde,
    });
  });

  return structured;
}

// ─── Validation ─────────────────────────────────────────────────────────────

/**
 * Validate all recordings and return structured warning objects:
 * { message: string, recordingId: string|null, boringName: string|null }
 *
 * Checks:
 *   - Duplicate boring names across recordings
 *   - Monsters without a name or identificatie
 */
export function validateProjectData(recordings) {
  const warnings = [];
  const boringNameMap = {}; // name -> [recordingId, ...]

  for (const recording of recordings) {
    if (!recording.result_data) continue;
    
    let data;
    try {
      data = typeof recording.result_data === 'string'
        ? JSON.parse(recording.result_data)
        : recording.result_data;
    } catch {
      continue;
    }

    for (const mp of (data.Meetpunten || [])) {
      const name = mp.name?.waarde || mp.identificatie?.waarde;
      if (name) {
        if (!boringNameMap[name]) boringNameMap[name] = [];
        boringNameMap[name].push(recording.recordingId || recording.id);
      }
    }
  }

  for (const [name, recordingIds] of Object.entries(boringNameMap)) {
    if (recordingIds.length > 1) {
      warnings.push({
        message: `Dubbele boring gevonden: "${name}" (${recordingIds.length}×)`,
        recordingId: recordingIds[0],
        boringName: name,
      });
    }
  }

  return warnings;
}

/**
 * Validate a single recording's result_data.
 * Returns an array of plain warning strings for display in the table row.
 */
export function validateRecordingData(recording) {
  return []; // Monster names are optional — no per-recording warnings needed currently
}
